import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  DestroyRef,
  inject,
  Renderer2,
  ElementRef,
  effect,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';

import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { ITableColumn } from 'src/app/models/tableColumns';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { TableColumnsNames } from 'src/app/models/tableColumns/tableColumnsNames.enum';
import { DataLayerTableColumns } from 'src/app/models/tableColumns/dataLayerTableColumn.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import {
  IDataLayerTableRowViewModel,
  DataLayerTableRowViewModelHelper,
} from 'src/app/models/viewModels/dataLayerTableRowViewModel';
import { DataLayerHttpService } from 'src/app/services/httpServices/dataLayerHttpService/data-layer-http.service';
import { IDataLayerItem } from 'src/app/models/api/dataLayer.model';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import { Labels } from 'src/app/labels/englishLabels';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { Stack } from 'src/app/common/dataStructures/stack';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { DataLayerFiltersModel } from 'src/app/models/filters/implementations/pageFilters/dataLayerFilters.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { ISelectOption } from 'src/app/models/selectOptions';
import { DataLayerItemType } from 'src/app/models/api/dataLayer.model';
import { SUPPORTED_FILE_EXTENSIONS } from 'src/app/common/dataLayer/dataLayerUtils';
import { FILE_EXTENSION_VALUE_PREFIX } from 'src/app/models/selectOptions/dataLayerItemTypeSelectOptions.model';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { FilterStorage } from 'src/app/services/filters/filter-storage';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { extractContinuationTokenFromURL } from 'src/app/common/exractContinuationTokenFromURL';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-data-lens',
  templateUrl: './data-layer.component.html',
  styleUrl: './data-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataLayerComponent implements OnInit {
  public readonly columns = signal<ITableColumn[]>(DataLayerTableColumns);
  public readonly pageName = TableColumnsNames.DataLayer;

  public readonly currentPage = signal<IDataLayerTableRowViewModel[]>([]);
  public readonly currentPage$: BehaviorSubject<IDataLayerTableRowViewModel[]>;
  public readonly isLoading = signal<boolean>(true);
  public readonly showNoData = signal<boolean>(false);

  public readonly defaultPageSize = signal<number>(this._dataLayerHttpService.defaultPageSize);
  public readonly pageInfo = signal<ITablePageInfo>({
    number: 1,
    maxSize: this.defaultPageSize(),
    currSize: 0,
    totalItems: 0,
    totalPages: 1,
  });

  public totalItems = signal<number>(0);
  public expandFilterResultControl = new FormControl<boolean>(false);
  public isFiltersMenuOpen = signal<boolean>(false);

  public pageFilters: DataLayerFiltersModel;

  readonly labels = Labels.dataLensPage;

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _paginationNavigation = signal<IPaginationNavigationModel | null>(null);
  private readonly _pageChange$ = new Subject<string>();

  constructor(
    private readonly _dataLayerHttpService: DataLayerHttpService,
    private readonly _sidePanelService: SidePanelService,
    private readonly _storeService: StoreService,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _logger: LoggerService,
    private readonly _httpErrorHandler: HttpErrorHandlerService
  ) {
    this.pageFilters = new DataLayerFiltersModel(
      this._router,
      this._dataLayerHttpService,
      this._displayCamelCaseStringPipe
    );

    this.currentPage$ = new BehaviorSubject<IDataLayerTableRowViewModel[]>([]);

    effect(() => {
      this.currentPage$.next(this.currentPage());
    });
  }

  ngOnInit(): void {
    this._initializeFiltersFromQueryParams();
    this._loadSensitivityLabels();
    this._setupFilterAndSortStream();
    this._setupPaginationStream();
    this._setupActiveFiltersHeightStream();
    this._setupFilterPersistence();
  }

  public tablesFiltersChange(options: ISelectOption[]): void {
    this.pageFilters.updateFilterFromUserSelection(undefined, options);
  }

  public onPagination(operation: PaginationOperationEnum): void {
    if (this.isLoading()) return;

    const navigation = this._paginationNavigation();
    if (!navigation) return;

    if (operation === PaginationOperationEnum.next) {
      navigation.previousPage.push(navigation.currentPage);
    }

    const pageUrl = operation === PaginationOperationEnum.next ? navigation.nextPage : navigation.previousPage.pop();

    const continuationToken = extractContinuationTokenFromURL(pageUrl ?? '', this._logger);
    if (continuationToken) {
      this._pageChange$.next(continuationToken);
    }
  }

  public rowSelected(item: IDataLayerTableRowViewModel): void {
    this._storeService.setSelectedDataLensItem(item);
    this._sidePanelService.openDataLensDetails(item);
  }

  public rowUnselected(): void {
    this._sidePanelService.close();
  }

  private _setupFilterAndSortStream(): void {
    combineLatest([this.pageFilters.onUpdate$, this.pageFilters.sortedColumn$])
      .pipe(
        tap(() => {
          this.currentPage.set([]);
          this.isLoading.set(true);
          this._paginationNavigation.set(null);
        }),
        switchMap(([filters, sort]: [ISelectedFilters, SortColumn | undefined]) =>
          this._dataLayerHttpService.getDataLayerItems(filters, sort)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<IDataLayerItem>) => this._handleDataLensResponse(response),
        error: (error) => this._handleDataLensError(error),
      });
  }

  private _setupPaginationStream(): void {
    this._pageChange$
      .pipe(
        tap(() => {
          this.currentPage.set([]);
          this.isLoading.set(true);
        }),
        switchMap((token: string) => this._dataLayerHttpService.getDataLayerItemsByContinuationToken(token)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<IDataLayerItem>) => this._handleDataLensResponse(response),
        error: (error) => this._handleDataLensError(error),
      });
  }

  private _setupActiveFiltersHeightStream(): void {
    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _initializeFiltersFromQueryParams(): void {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    let filtersQuery = queryParams['q'];

    if (!filtersQuery) {
      const saved = FilterStorage.load('data-lens');
      if (saved) {
        filtersQuery = saved;
      }
    }

    if (filtersQuery) {
      const parsedFilters = FiltersQueryTransformer.transformFromQuery(filtersQuery);
      if (Object.keys(parsedFilters).length > 0) {
        this.pageFilters.updateFiltersByKeyValue(parsedFilters, false);
      }
    } else {
      this._applyDefaultFilters();
    }
  }

  private _setupFilterPersistence(): void {
    this.pageFilters.onUpdate$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((selectedFilters) => {
      const q = FiltersQueryTransformer.transformToQuery(selectedFilters);
      FilterStorage.save('data-lens', q);
    });
  }

  private _applyDefaultFilters(): void {
    const defaultDataTypes = [DataLayerItemType.website];
    const defaultFileExtensions = [...SUPPORTED_FILE_EXTENSIONS].map((ext) => `${FILE_EXTENSION_VALUE_PREFIX}${ext}`);
    const combinedValues = [...defaultDataTypes, ...defaultFileExtensions];

    this.pageFilters.dataTypeFilters.updateByValuesIfNeeded(combinedValues);
    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.pageFilters.getSelectedFilters());
    this._router.navigate([], {
      queryParams: { q: filtersQuery },
      queryParamsHandling: 'merge',
    });
  }

  private _loadSensitivityLabels(): void {
    this._dataLayerHttpService
      .getSensitivityLabels()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (labels: string[]) => {
          this.pageFilters.updateSensitivityLabelOptions(labels);
          this._initializeFiltersFromQueryParams();
        },
        error: (error) => {
          this._logger.error('Error loading sensitivity labels:', error);
        },
      });
  }

  private _handleDataLensResponse(response: JsonHalCollection<IDataLayerItem>): void {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);

    const items = response._embedded?.items || [];

    if (!items || items.length === 0) {
      this.showNoData.set(true);
      this.currentPage.set([]);
    } else {
      this.showNoData.set(false);
      const viewModels = items.map((item) => DataLayerTableRowViewModelHelper.createViewModel(item));
      this.currentPage.set(viewModels);
    }

    this.isLoading.set(false);
  }

  private _handlePaginationUrls(links: JsonHalLinks): void {
    const currentNavigation = this._paginationNavigation();

    if (!currentNavigation) {
      this._paginationNavigation.set({
        currentPage: links.self.href,
        nextPage: links.next?.href && links.next.href !== '' ? links.next.href : undefined,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel);
    } else {
      this._paginationNavigation.set({
        ...currentNavigation,
        currentPage: links.self.href,
        nextPage: links.next?.href && links.next.href !== '' ? links.next.href : undefined,
      });
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo): void {
    this.totalItems.set(pageInfo.totalItems);
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize());
    this.pageInfo.set(tablePageInfo);
  }

  private _handleDataLensError(error: HttpErrorResponse): void {
    this.currentPage.set([]);
    this.totalItems.set(0);
    this.showNoData.set(true);
    this.isLoading.set(false);
    this._httpErrorHandler.handle(error);
  }
}
